import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ModalService } from '../../../../../../services/modal.service';
import { QuickLinkType } from '../../../../../../models/quick-link';
import { DisabledFeatureModalable } from '../../../../../../models/disabled-feature-modalable';
import { Modalable } from '../../../../../../models/modalable';
import { AssetPipe } from '../../../../../../pipes/asset.pipe';
import { SafeHtmlPipe } from '../../../../../../pipes/safe-html.pipe';
import { NgClass } from '@angular/common';

interface ItemType {
    type: string | QuickLinkType;
    iconClass?: string;
    imageSrc?: string;
    imageClass?: string;
    altText?: string;
    headerText: string;
}

@Component({
    selector: 'app-feature-disabled-header',
    templateUrl: './feature-disabled-header.component.html',
    styleUrls: ['./feature-disabled-header.component.scss'],
    imports: [
        AssetPipe,
        SafeHtmlPipe,
        NgClass
    ],
    standalone: true
})
export class FeatureDisabledHeaderComponent implements OnChanges {
    @Input() modalable: Modalable;
    selectedItem: ItemType | null;
    defaultIcon = 'draw-mode/icon-tool-drawing.png';

    typeMappings: Array<ItemType> = [
        {
            type: ModalService.E_SQUIRREL,
            imageSrc: '/sidebar-quicklinks/esquirrel-logo.png',
            altText: 'Das Logo von eSquirrel',
            headerText: 'eSquirrel'
        },
        {
            type: ModalService.CALCUFLOW,
            imageSrc: '/sidebar/sidebar-icon-calcuflow.png',
            altText: 'Calcuflow Logo',
            headerText: 'Arbeitsblatt-Editor'
        },
        {
            type: ModalService.PHASE,
            imageSrc: '/sidebar-quicklinks/phase-6-logo.svg',
            altText: 'Das Logo von Phase-6',
            headerText: 'phase6',
            imageClass: 'phase-6'
        },
        {
            type: ModalService.SIA,
            imageSrc: '/sidebar-quicklinks/sia-logo.png',
            altText: 'Das Logo von SIA Eductation',
            headerText: 'SIA Education'
        },
        {
            type: ModalService.GMT,
            imageSrc: '/sidebar-quicklinks/icon-genial-mathe-trainer.png',
            altText: 'Das Logo von GMT',
            headerText: 'Genial! Mathe-Trainer'
        },
        {
            type: ModalService.DIGI,
            imageSrc: 'sidebar/sidebar-icon-digitrainer.png',
            altText: 'Das Logo vom digi.trainer',
            headerText: 'Dazu passender digi.trainer'
        },
        {
            type: ModalService.HSM,
            imageSrc: '/sidebar-quicklinks/icon-hsm-color.png',
            altText: 'Das Logo vom Aufgabenmanager',
            headerText: 'Aufgabenmanager'
        },
        {
            type: ModalService.OTHER,
            imageSrc: '/sidebar-quicklinks/icon-hsm-color.png',
            altText: 'Das Logo vom Aufgabenmanager',
            headerText: 'Aufgabenmanager'
        },
        {
            type: ModalService.MATERIALS,
            imageSrc: 'topbar-icon-materialien.png',
            altText: 'Das Logo von Materials',
            headerText: 'Materialen <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.LINK,
            imageSrc: 'link-modal/large-link-icon-weblink.png',
            altText: 'Das Logo von Links',
            headerText: 'Links <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.VIDEO,
            imageSrc: 'link-modal/large-link-icon-video.png',
            altText: 'Das Logo von Videos',
            headerText: 'Videos <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.AUDIO,
            imageSrc: 'link-modal/large-link-icon-audio-play.png',
            altText: 'Das Logo von Audios',
            headerText: 'Audios <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.SCHOOL_BAG,
            iconClass: 'fa fa-book heading',
            altText: 'Das Logo von Schultasche',
            headerText: 'Schultasche <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.EXPORT_PAGES,
            iconClass: 'fa fa-download heading',
            altText: 'Das Logo von Seite exportieren',
            headerText: 'Seite exportieren <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.HIGHLIGHT_TEXT,
            imageSrc: this.defaultIcon,
            headerText: 'Text markieren <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.DIGI_TRAINER_LINK,
            imageSrc: 'sidebar/sidebar-digitrainer.png',
            headerText: 'DIGI.TRAINER'
        },
        {
            type: ModalService.CREATE_NOTE,
            imageSrc: this.defaultIcon,
            headerText: 'Notiz erstellen <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.PAGE_FAVORITE,
            imageSrc: this.defaultIcon,
            headerText: 'Lesezeichen verwenden <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.NOTES,
            imageSrc: this.defaultIcon,
            headerText: 'Notizen anzeigen <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.SEARCH,
            imageSrc: this.defaultIcon,
            headerText: 'Suchen <br/>+ alle weiteren Buchfunktionen'
        },
        {
            type: ModalService.YEDO,
            imageSrc: '/sidebar-quicklinks/yedo-logo.png',
            altText: 'Yedo.at',
            headerText: 'Alle Inhalte von Yedo nutzen'
        },
        {
            type: ModalService.TEST_GENERATOR,
            imageSrc: '/sidebar/test-generator.png',
            altText: 'Das Logo von Test Generator',
            headerText: 'Tests & Wiederholungen'
        },
        {
            type: ModalService.EDU_SUITE,
            imageSrc: '/sidebar-quicklinks/edusuite-logo.png',
            altText: 'Lemberger Adaptive Mathematik',
            headerText: 'Alle Inhalte von Lemberger Adaptive Mathematik nutzen'
        },
        {
            type: ModalService.MATHION,
            imageSrc: '/sidebar-quicklinks/mathion-logo.png',
            altText: 'Mathion.at',
            headerText: 'Alle Inhalte von Mathion nutzen'
        },
        {
            type: ModalService.DIGBI,
            imageSrc: '/sidebar-quicklinks/digbi-logo.png',
            altText: 'digbi.net',
            headerText: 'Alle Inhalte von Digbi nutzen'
        },
        {
            type: ModalService.MATHE_GYM,
            imageSrc: '/sidebar-quicklinks/matheGym-logo.png',
            altText: 'mathegym.de',
            headerText: 'Alle Inhalte von MatheGym nutzen'
        },
        {
            type: ModalService.MATHE4ALLE,
            imageSrc: '/sidebar-quicklinks/mathe4Alle-logo.png',
            altText: 'mathe4alle.at',
            headerText: 'Alle Inhalte von mathe4alle nutzen'
        },
        {
            type: null,
            imageSrc: this.defaultIcon,
            headerText: 'Zeichnen & Stempeln <br/>+ alle weiteren Buchfunktionen'
        }
    ];

    constructor(private modalService: ModalService) {
        this.getText();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.getText();
    }

    findItemByType(type: string | null | QuickLinkType): any {
        return this.typeMappings.find(item => item.type === type);
    }

    private getText() {
        if (this.modalable instanceof DisabledFeatureModalable) {
            this.defaultIcon = this.modalable.iconUrl;
        }
        this.selectedItem = this.findItemByType(this.modalService.getModalType(this.modalable)) || this.findItemByType(null);
    }
}
